(() => {
  if (window.AbokMobileModalController) return;

  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const bindings = new Map();
  let scanFrame = null;

  const scrollLock = {
    locked: false,
    scrollX: 0,
    scrollY: 0,
    styles: null,

    lock() {
      if (this.locked) return;

      const body = document.body;
      const root = document.documentElement;
      const properties = ['position', 'top', 'right', 'left', 'width', 'overflow', 'padding-right'];

      this.locked = true;
      this.scrollX = window.scrollX;
      this.scrollY = window.scrollY;
      this.styles = {
        body: properties.map((property) => ({
          property,
          value: body.style.getPropertyValue(property),
          priority: body.style.getPropertyPriority(property),
        })),
        rootOverflow: root.style.getPropertyValue('overflow'),
        rootOverflowPriority: root.style.getPropertyPriority('overflow'),
      };

      const scrollbarWidth = window.innerWidth - root.clientWidth;
      const currentPadding = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      root.classList.add('abok-mobile-modal-open');
      body.classList.add('abok-mobile-modal-open');
      root.style.setProperty('overflow', 'hidden');
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-this.scrollY}px`);
      body.style.setProperty('right', '0');
      body.style.setProperty('left', '0');
      body.style.setProperty('width', '100%');
      body.style.setProperty('overflow', 'hidden');
      if (scrollbarWidth > 0) body.style.setProperty('padding-right', `${currentPadding + scrollbarWidth}px`);
    },

    unlock() {
      if (!this.locked) return;

      const body = document.body;
      const root = document.documentElement;
      const scrollX = this.scrollX;
      const scrollY = this.scrollY;
      const rootScrollBehavior = root.style.getPropertyValue('scroll-behavior');
      const rootScrollBehaviorPriority = root.style.getPropertyPriority('scroll-behavior');

      this.styles.body.forEach(({ property, value, priority }) => {
        if (value) {
          body.style.setProperty(property, value, priority);
        } else {
          body.style.removeProperty(property);
        }
      });

      if (this.styles.rootOverflow) {
        root.style.setProperty('overflow', this.styles.rootOverflow, this.styles.rootOverflowPriority);
      } else {
        root.style.removeProperty('overflow');
      }

      root.classList.remove('abok-mobile-modal-open');
      body.classList.remove('abok-mobile-modal-open');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(scrollX, scrollY);

      if (rootScrollBehavior) {
        root.style.setProperty('scroll-behavior', rootScrollBehavior, rootScrollBehaviorPriority);
      } else {
        root.style.removeProperty('scroll-behavior');
      }

      this.locked = false;
      this.styles = null;
    },
  };

  function isVisible(element) {
    if (!element || element.hidden || element.closest('[hidden]')) return false;

    const styles = window.getComputedStyle(element);
    const box = element.getBoundingClientRect();
    return styles.display !== 'none' && styles.visibility !== 'hidden' && box.width > 0 && box.height > 0;
  }

  function callClose(root) {
    if (!root?.isConnected) return;

    if (root.id === 'shopify-pc__prefs') {
      root.querySelector('#shopify-pc__prefs__header-close')?.click();
      return;
    }

    if (typeof root.hide === 'function') {
      root.hide();
      return;
    }

    if (typeof root.close === 'function') {
      root.close();
      return;
    }

    const closeControl = root.querySelector(
      '[data-modal-close], [data-cart-disclosure-close], [data-abok-drawer-close], '
      + '[class*="__close"], button[aria-label*="close" i], button[aria-label*="schlie" i]'
    );

    if (closeControl) {
      closeControl.click();
    } else {
      root.dispatchEvent(new KeyboardEvent('keyup', { key: 'Escape', code: 'Escape', bubbles: true }));
    }
  }

  function collectOpenDialogs() {
    const descriptors = [];
    const handledRoots = new Set();
    const handledSheets = new Set();

    const add = (root, sheet, { gesture = true } = {}) => {
      if (!root || !sheet || handledSheets.has(sheet)) return;
      handledRoots.add(root);
      handledSheets.add(sheet);
      descriptors.push({ root, sheet, gesture, close: () => callClose(root) });
    };

    document.querySelectorAll('modal-element[open]').forEach((root) => {
      add(root, root.querySelector('[data-modal-dialog]') || root, {
        gesture: !root.matches('[data-abok-site-sheet]'),
      });
    });

    document.querySelectorAll('modal-dialog[open], quick-add-modal[open], product-modal[open]').forEach((root) => {
      add(root, root.querySelector('[role="dialog"]') || root);
    });

    document.querySelectorAll('details-modal, password-modal').forEach((root) => {
      const details = root.querySelector('details[open]');
      if (details) add(root, details.querySelector('[role="dialog"]') || details);
    });

    document.querySelectorAll('cart-drawer.active').forEach((root) => {
      add(root, root.querySelector('[role="dialog"]') || root);
    });

    document.querySelectorAll('cart-notification').forEach((root) => {
      const sheet = root.querySelector('[role="dialog"].active');
      if (sheet) add(root, sheet);
    });

    document.querySelectorAll('cart-disclosure-modal[open], pickup-availability-drawer[open]').forEach((root) => {
      add(root, root.querySelector('[role="dialog"]') || root);
    });

    document.querySelectorAll('[data-abok-drawer].is-open').forEach((root) => {
      add(root, root.querySelector('[data-abok-drawer-panel]') || root, { gesture: false });
    });

    const privacyRoot = document.querySelector('#shopify-pc__prefs');
    const privacyDialog = privacyRoot?.querySelector('#shopify-pc__prefs__dialog');
    if (isVisible(privacyDialog)) add(privacyRoot, privacyDialog);

    document.querySelectorAll('[role="dialog"][aria-modal="true"]').forEach((dialog) => {
      if (handledSheets.has(dialog) || !isVisible(dialog)) return;
      if ([...handledRoots].some((root) => root === dialog || root.contains(dialog))) return;
      if (dialog.closest(
        'modal-element, modal-dialog, quick-add-modal, product-modal, details-modal, password-modal, '
        + 'cart-drawer, cart-notification, cart-disclosure-modal, pickup-availability-drawer, '
        + '[data-abok-drawer], [data-abok-site-sheet], #shopify-pc__prefs'
      )) return;
      add(dialog, dialog);
    });

    return descriptors;
  }

  function createHandle(sheet) {
    const semanticHeader = [...sheet.querySelectorAll(
      ':scope > header, :scope > .drawer__header, :scope > .cart-notification__header, '
      + ':scope > .cart-disclosure-modal__header'
    )].find(isVisible);
    const handle = semanticHeader || document.createElement('span');
    const generated = !semanticHeader;
    const originalPosition = handle.style.getPropertyValue('position');
    const originalPositionPriority = handle.style.getPropertyPriority('position');

    if (generated) {
      handle.className = 'abok-mobile-modal__drag-handle';
      handle.setAttribute('aria-hidden', 'true');
      sheet.prepend(handle);
    } else if (window.getComputedStyle(handle).position === 'static') {
      handle.style.setProperty('position', 'relative');
    }

    handle.setAttribute('data-abok-mobile-modal-handle', '');
    const indicator = document.createElement('span');
    indicator.className = 'abok-mobile-modal__drag-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    handle.prepend(indicator);

    return () => {
      indicator.remove();
      handle.removeAttribute('data-abok-mobile-modal-handle');
      if (generated) {
        handle.remove();
      } else if (originalPosition) {
        handle.style.setProperty('position', originalPosition, originalPositionPriority);
      } else {
        handle.style.removeProperty('position');
      }
    };
  }

  function bindGesture(descriptor) {
    const { sheet, close } = descriptor;
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const removeHandle = createHandle(sheet);
    let gesture = null;
    let animation = null;
    let suppressClick = false;

    sheet.setAttribute('data-abok-mobile-modal-sheet', '');
    const handle = sheet.querySelector('[data-abok-mobile-modal-handle]');

    const clearVisualState = () => {
      animation?.cancel();
      animation = null;
      sheet.style.removeProperty('translate');
      sheet.removeAttribute('data-abok-mobile-modal-dragging');
    };

    const animateBack = (delta) => {
      if (delta <= 0 || reducedMotionQuery.matches || typeof sheet.animate !== 'function') {
        clearVisualState();
        return;
      }

      animation = sheet.animate(
        [{ translate: `0 ${delta}px` }, { translate: '0 0' }],
        { duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
      sheet.style.removeProperty('translate');
      animation.finished.catch(() => {}).then(clearVisualState);
    };

    const animateClose = (delta) => {
      const finish = () => {
        clearVisualState();
        close();
        scheduleScan();
      };

      if (reducedMotionQuery.matches || typeof sheet.animate !== 'function') {
        finish();
        return;
      }

      const box = sheet.getBoundingClientRect();
      const endDelta = Math.max(delta + 120, window.innerHeight - box.top + 32);
      animation = sheet.animate(
        [{ translate: `0 ${delta}px` }, { translate: `0 ${endDelta}px` }],
        { duration: 180, easing: 'cubic-bezier(0.7, 0, 0.84, 0)' }
      );
      sheet.style.removeProperty('translate');
      animation.finished.catch(() => {}).then(finish);
    };

    handle.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick = false;
    }, { ...options, capture: true });

    handle.addEventListener('pointerdown', (event) => {
      if (event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target instanceof Element && event.target.closest(
        'button, a, input, select, textarea, label, [contenteditable="true"], [data-abok-mobile-modal-no-drag]'
      )) return;

      animation?.cancel();
      animation = null;
      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastDelta: 0,
        lastTime: performance.now(),
        velocity: 0,
        delta: 0,
        moved: false,
        vertical: false,
        rejected: false,
      };
      handle.setPointerCapture?.(event.pointerId);
    }, options);

    handle.addEventListener('pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      if (!gesture.moved && Math.hypot(deltaX, deltaY) >= 6) {
        gesture.moved = true;
        gesture.rejected = Math.abs(deltaX) >= Math.abs(deltaY) * 0.85;
        gesture.vertical = !gesture.rejected;
      }

      if (!gesture.vertical || gesture.rejected) return;

      const nextDelta = Math.max(0, deltaY);
      const now = performance.now();
      const elapsed = Math.max(1, now - gesture.lastTime);
      gesture.velocity = (nextDelta - gesture.lastDelta) / elapsed;
      gesture.delta = nextDelta;
      gesture.lastDelta = nextDelta;
      gesture.lastTime = now;
      sheet.setAttribute('data-abok-mobile-modal-dragging', '');
      sheet.style.setProperty('translate', `0 ${nextDelta.toFixed(1)}px`);
      event.preventDefault();
    }, { ...options, passive: false });

    const finishGesture = (event, cancelled = false) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      if (handle.hasPointerCapture?.(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      if (gesture.moved) {
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 0);
      }

      const completedGesture = gesture;
      gesture = null;
      sheet.removeAttribute('data-abok-mobile-modal-dragging');

      const distanceThreshold = Math.min(140, Math.max(84, sheet.offsetHeight * 0.16));
      const fastDownwardFlick = completedGesture.delta >= 32 && completedGesture.velocity >= 0.65;
      const shouldClose = !cancelled
        && completedGesture.vertical
        && (completedGesture.delta >= distanceThreshold || fastDownwardFlick);

      if (shouldClose) {
        animateClose(completedGesture.delta);
      } else {
        animateBack(completedGesture.delta);
      }
    };

    handle.addEventListener('pointerup', (event) => finishGesture(event), options);
    handle.addEventListener('pointercancel', (event) => finishGesture(event, true), options);

    return () => {
      abortController.abort();
      clearVisualState();
      removeHandle();
      sheet.removeAttribute('data-abok-mobile-modal-sheet');
    };
  }

  function scan() {
    scanFrame = null;
    if (!mobileQuery.matches) {
      bindings.forEach((cleanup) => cleanup());
      bindings.clear();
      scrollLock.unlock();
      return;
    }

    const descriptors = collectOpenDialogs();
    const activeSheets = new Set(descriptors.map(({ sheet }) => sheet));

    bindings.forEach((cleanup, sheet) => {
      if (activeSheets.has(sheet)) return;
      cleanup();
      bindings.delete(sheet);
    });

    descriptors.forEach((descriptor) => {
      if (!descriptor.gesture || bindings.has(descriptor.sheet)) return;
      bindings.set(descriptor.sheet, bindGesture(descriptor));
    });

    if (descriptors.length > 0) {
      scrollLock.lock();
    } else {
      scrollLock.unlock();
    }
  }

  function scheduleScan() {
    if (scanFrame !== null) return;
    scanFrame = window.requestAnimationFrame(scan);
  }

  function initialize() {
    const modalCandidateSelector = [
      'modal-element',
      'modal-dialog',
      'quick-add-modal',
      'product-modal',
      'details-modal',
      'password-modal',
      'cart-drawer',
      'cart-notification',
      'cart-disclosure-modal',
      'pickup-availability-drawer',
      '[data-abok-drawer]',
      '#shopify-pc__prefs',
      '[role="dialog"][aria-modal="true"]',
    ].join(', ');

    const containsModalCandidate = (node) => {
      return node instanceof Element
        && (node.matches(modalCandidateSelector) || Boolean(node.querySelector(modalCandidateSelector)));
    };

    const observer = new MutationObserver((mutations) => {
      const modalStateChanged = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          return [...mutation.addedNodes, ...mutation.removedNodes].some(containsModalCandidate);
        }

        if (!(mutation.target instanceof Element)) return false;
        if (
          mutation.attributeName === 'style'
          && mutation.target.hasAttribute('data-abok-mobile-modal-sheet')
        ) return false;

        return mutation.target.matches(modalCandidateSelector)
          || Boolean(mutation.target.closest(modalCandidateSelector));
      });
      if (modalStateChanged) scheduleScan();
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['active', 'aria-hidden', 'class', 'hidden', 'inert', 'open', 'style'],
    });

    const onBreakpointChange = () => scheduleScan();
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else {
      mobileQuery.addListener(onBreakpointChange);
    }
    window.addEventListener('pageshow', scheduleScan);
    document.addEventListener('shopify:section:load', scheduleScan);
    scheduleScan();

    window.AbokMobileModalController = {
      scan: scheduleScan,
      get activeCount() {
        return collectOpenDialogs().length;
      },
      get scrollLocked() {
        return scrollLock.locked;
      },
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
